import algosdk from "algosdk";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from "../context/WalletContext";
import { useTransaction } from "../context/TransactionContext";
import "../styles/stake.css";

const ASA_STAKE = 748884896;
const ASA_REWARD = 748885409;
const DECIMALS = 1e9;
const REWARD_RATE = 6_048_000_000; 
const appId = 748946206;

const Staking = ({ algodClient, setMessage }: { algodClient: algosdk.Algodv2, setMessage: (msg: string) => void }) => {
  const { account } = useWallet();
  const navigate = useNavigate();
  const { openTransactionModal } = useTransaction();

  const [isOptedIn, setIsOptedIn] = useState(false);
  const [amount, setAmount] = useState<string>("");
  const [timeElapsed, setTimeElapsed] = useState<number | null>(null);
  const [hasStaked, setHasStaked] = useState(false);

  const fetchTimeElapsed = useCallback(async () => {
  if (!account) return;

  try {
    const res = await algodClient.accountApplicationInformation(account.addr, appId).do();
    const localState = res["app-local-state"]?.["key-value"] || [];

    const montoKV = localState.find((kv: any) => atob(kv.key) === "monto_stake");
    const inicioKV = localState.find((kv: any) => atob(kv.key) === "inicio_stake");

    const monto = montoKV?.value?.uint ?? 0;
    const inicio = inicioKV?.value?.uint ?? 0;

    if (monto > 0 && inicio > 0) {
      const now = Math.floor(Date.now() / 1000);
      const diff = now - inicio;
      setTimeElapsed(diff > 0 ? diff : 0);
      setHasStaked(true);
    } else {
      setTimeElapsed(null);
      setHasStaked(false);
    }
  } catch (err) {
    console.error("Error leyendo estado local:", err);
  }
}, [account]);


  useEffect(() => {
    fetchTimeElapsed();
  }, [fetchTimeElapsed]);

  useEffect(() => {
  if (!hasStaked || timeElapsed === null) return;

  const interval = setInterval(() => {
    setTimeElapsed((prev) => (prev !== null ? prev + 1 : null));
  }, 1000);

  return () => clearInterval(interval);
}, [hasStaked, timeElapsed]);

  const handleOptIn = async () => {
    if (!account) return;
    try {
      const info = await algodClient.accountInformation(account.addr).do();
      const alreadyOptedIn = info["apps-local-state"]?.some((app: any) => app.id === appId);
      if (alreadyOptedIn) {
        setMessage("✅ Ya estás activado para staking");
        setIsOptedIn(true);
        return;
      }

      const suggestedParams = await algodClient.getTransactionParams().do();
      const optInTxn = algosdk.makeApplicationOptInTxn(account.addr, suggestedParams, appId);
      const signedTxn = optInTxn.signTxn(account.sk);
      const { txId } = await algodClient.sendRawTransaction(signedTxn).do();
      setMessage(`✅ Opt-in realizado - TxID: ${txId}`);
      setIsOptedIn(true);
    } catch (err) {
      console.error("❌ Error en opt-in:", err);
      setMessage("❌ Error al activar el staking");
    }
  };

  const handleStake = async () => {
  if (!account) {
    setMessage("⚠️ Conecta tu cuenta para continuar");
    return;
  }

  const numericAmount = parseFloat(amount) || 0;
  if (isNaN(numericAmount) || numericAmount <= 0) {
    setMessage("⚠️ Ingresa una cantidad válida de UMQ");
    return;
  }

  const amountInMicro = Math.floor(numericAmount * DECIMALS);

  try {
    const suggestedParams = await algodClient.getTransactionParams().do();

    const stakeTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
      from: account.addr,
      to: algosdk.getApplicationAddress(appId),
      amount: amountInMicro,
      assetIndex: ASA_STAKE,
      suggestedParams,
    });

    const appCallTxn = algosdk.makeApplicationNoOpTxnFromObject({
      from: account.addr,
      appIndex: appId,
      appArgs: [new TextEncoder().encode("do_stake")],
      foreignAssets: [ASA_STAKE],
      suggestedParams,
    });

    const txns = [stakeTxn, appCallTxn];
    algosdk.assignGroupID(txns);
    const signedTxns = txns.map((txn) => txn.signTxn(account.sk));
    const { txId } = await algodClient.sendRawTransaction(signedTxns).do();

    setMessage(`✅ Stake enviado correctamente - TxID: ${txId}`);
    localStorage.setItem("stakingAmount", amount);
    setHasStaked(true);
    setTimeElapsed(0); 
  } catch (error: any) {
    console.error("❌ Error al hacer stake:", error);
    const raw = error?.response?.data?.message || error?.message || "Error desconocido";
    if (raw.includes("assert failed")) {
      setMessage("❌ El stake falló: asegúrate de enviar al menos 100 y haber hecho opt-in al ASA");
    } else if (raw.includes("overspend")) {
      setMessage("❌ No tienes suficientes UMQ o ALGO para cubrir la transacción");
    } else {
      setMessage(`❌ Error al hacer stake: ${raw}`);
    }
  }
};

  const handleWithdraw = async () => {
  if (!account) return;

  try {
    const res = await algodClient.accountApplicationInformation(account.addr, appId).do();
    const localState = res["app-local-state"]?.["key-value"] || [];

    const montoKV = localState.find((kv: any) => atob(kv.key) === "monto_stake");
    const stakeAmount = montoKV?.value?.uint ?? 0;

    if (stakeAmount === 0) {
      setMessage("⚠️ No tienes UMQ stakeado actualmente.");
      return;
    }

    const appAddress = algosdk.getApplicationAddress(appId);

    openTransactionModal({
      recipient: appAddress,
      amount: stakeAmount / DECIMALS,
      asset: { name: "UMQ", id: ASA_STAKE, decimals: 9 },
      onConfirm: async () => {
        try {
          const params = await algodClient.getTransactionParams().do();

          const feeTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
            from: account.addr,
            to: appAddress,
            amount: 15 * DECIMALS,
            assetIndex: ASA_STAKE,
            suggestedParams: params,
          });

          const appCallTxn = algosdk.makeApplicationNoOpTxnFromObject({
            from: account.addr,
            appIndex: appId,
            appArgs: [new TextEncoder().encode("withdraw_stake")],
            foreignAssets: [ASA_STAKE, ASA_REWARD],
            suggestedParams: params,
          });

          const txns = [feeTxn, appCallTxn];
          algosdk.assignGroupID(txns);
          const signedTxns = txns.map((txn) => txn.signTxn(account.sk));
          const { txId } = await algodClient.sendRawTransaction(signedTxns).do();

          setMessage(`✅ Retiro realizado. TxID: ${txId}`);
          setHasStaked(false);
          setTimeElapsed(null);
          setAmount("");
          localStorage.removeItem("stakingAmount");

          await fetchTimeElapsed(); // 🔁 Actualiza el contador
        } catch (err: any) {
          console.error("❌ Error al enviar transacción:", err);
          const raw = err?.response?.data?.message || err?.message || "Error desconocido";
          if (raw.includes("assert failed")) {
            setMessage("❌ El contrato rechazó el retiro. Verifica si ya retiraste o si el monto es inválido.");
          } else {
            setMessage("❌ Error al retirar stake: " + raw);
          }
        }
      },
    });
  } catch (err) {
    console.error("❌ Error obteniendo estado local:", err);
    setMessage("❌ No se pudo verificar tu stake actual.");
  }
};

  const formatTime = (seconds: number) => {
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${d}d ${h}h ${m}m ${s}s`;
  };

  const estimatedReward = (amount: number, timeElapsed: number) => {
  const rawReward = amount * timeElapsed * REWARD_RATE;
  return rawReward / (DECIMALS * DECIMALS); 
};

useEffect(() => {
  const storedAmount = localStorage.getItem("stakingAmount");
  if (storedAmount) {
    setAmount(storedAmount);
  }
}, []);


  return (
    <div className="stake-container">
      <h2>Staking UMQ</h2>

      {!isOptedIn ? (
        <>
          <p>Para comenzar, activa tu participación en el contrato.</p>
          <button  disabled={false} onClick={handleOptIn} className="button-primary">Activar Staking</button>
        </>
      ) : (
        <>
          <input
            type="number"
            placeholder="Cantidad mínima 100"
            value={amount}
            min={100}
            step={1}
            onChange={(e) => setAmount(e.target.value)}
            className="wallet-input no-spinner"
            disabled={hasStaked}
            style={hasStaked ? { opacity: 0.6, cursor: "not-allowed" } : {}}
          />
          <button
            onClick={handleStake}
            className="button-primary"
            disabled={hasStaked}
            style={hasStaked ? { opacity: 0.6, cursor: "not-allowed" } : {}}
          >
            Hacer Stake
          </button>
          {hasStaked && timeElapsed !== null && (
            <>
              <button onClick={handleWithdraw} className="button-secondary">Retirar Stake</button>
               <div className="staking-info-wrapper">
  <table className="staking-info-table">
    <thead>
      <tr>
        <th>⏳ Tiempo en staking</th>
        <th>🎁 Recompensa estimada</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>{formatTime(timeElapsed)}</strong></td>
      <td><strong>{estimatedReward(parseFloat(amount) || 0, timeElapsed || 0).toFixed(9)} HLK</strong></td>
      </tr>
    </tbody>
  </table>
</div>
            </>
          )}
        </>
      )}
      <button className="button-exit" onClick={() => navigate("/home")}>Volver</button>
    </div>
  );
};

export default Staking;


